import {
  getCookie,
  hasWindow,
  getFromSessionStorage,
  saveToSessionStorage,
  OnUnloadCaller,
  isMobileRegEx,
} from "@litres/utils";
import axios from "axios";
import type {
  ITrackerConfig,
  ITrackerRequestData,
  IEventData,
  IWindowGlobals,
  TEventMapCommon,
  IEvent,
} from "./TrackerManager.types";
import {
  getTrackerEvent,
  prepareFullEventDataForSending,
} from "./TrackerEvent";
import { getTrackerApiUrl } from "./utils";

const HOSTS_WHERE_EVENTS_MUST_BE_TRACKED = new Set([
  "www.litres.ru",
  "litres.uz",
  "litres.az",
  "litres-tr.com",
  "litres.de",
  "litres.com",
  "litres.ee",
  "litres.es",
  "litres.pl",
  "litres.ua",
]);

/**
 * Провайдер подключения трекеров и отправки трекинга в Литрес Трекер
 * Создает синглтон, чтобы быть уверенными, что не подключим трекеры несколько раз
 * https://addyosmani.com/resources/essentialjsdesignpatterns/book/#observerpatternjavascript
 */
class TrackerManager {
  constructor(sendingEventsInterval = 2000, maxRetries = 2) {
    this.endpoint = getTrackerApiUrl();
    this.configuration = {
      abVariants: {},
      deviceModel: "Unknown user agent",
      host: "web",
      userID: 0,
      isTester: false,
      subscriptionUserStatus: {
        subscription: "inactive",
        abonement: "inactive",
      },
      registrationDate: "Unknown registration date",
    };
    this.queue = [];
    this.isSendingEventsFromQueue = false;
    this.sendingEventsInterval = sendingEventsInterval;
    this.maxRetries = maxRetries;
    this.proceedingEventUUIDs = new Set();
    this.isTrackedHost =
      hasWindow && HOSTS_WHERE_EVENTS_MUST_BE_TRACKED.has(window.location.host);
  }

  private endpoint: string;

  private configuration: ITrackerConfig;

  private static instance?: TrackerManager;

  private queue: IEvent[];

  private sendingEventsInterval: number;

  private timer?: NodeJS.Timeout;

  private maxRetries: number;

  private isSendingEventsFromQueue: boolean;

  private STORAGE_KEY = "tracker_events";

  private onUnloadCaller?: OnUnloadCaller;

  private proceedingEventUUIDs: Set<string>;

  private isTrackedHost: boolean;

  /** Метод, возвращающий единичный экземпляр синглтона */
  public static getInstance(): TrackerManager {
    if (TrackerManager.isInstanceOnWindow()) {
      const globalWindow = window as IWindowGlobals;
      TrackerManager.instance = globalWindow.TM;
    }

    if (!TrackerManager.instance) {
      TrackerManager.instance = new TrackerManager();

      if (hasWindow) {
        const globalWindow = window as IWindowGlobals;
        globalWindow.TM = TrackerManager.instance;

        if (document.readyState === "complete") {
          TrackerManager.instance.startSendEventsTimer();
        } else {
          window.addEventListener(
            "load",
            TrackerManager.instance.startSendEventsTimer.bind(
              TrackerManager.instance
            )
          );
        }
        if (TrackerManager.instance) {
          TrackerManager.instance.setupUnloadHandler();
        }
      }
    }

    return TrackerManager.instance;
  }

  private setupUnloadHandler() {
    this.onUnloadCaller = new OnUnloadCaller(() => {
      this.sendEventsFromQueueSync();
    });
    this.onUnloadCaller.subscribe();
  }

  private static isInstanceOnWindow(): boolean {
    return hasWindow && !!(window as IWindowGlobals).TM;
  }

  private isSendingEventsAvailable(): boolean {
    if (!hasWindow) {
      return false;
    }

    const isTrackingEnabledByCookie = Boolean(getCookie("tracker"));

    const isTrackingBlockedForTester =
      (this.configuration.isTester || process.env.NODE_ENV === "development") &&
      !isTrackingEnabledByCookie;

    if (isTrackingBlockedForTester) {
      return false;
    }

    return (
      this.isTrackedHost ||
      isTrackingEnabledByCookie ||
      Boolean(this.configuration.unittest)
    );
  }

  /**
   * Метод для установки конфига с трекерами, которые мы хотим обрабатывать
   * @param config ITrackerConfig
   */
  setConfiguration(config: ITrackerConfig) {
    // переопределить host для старого стека, где нет проброса browser.pda в redux state
    // @ts-expect-error
    if (window.__IS_NEXT__) {
      this.configuration = { ...this.configuration, ...config };
    } else if (hasWindow) {
      this.configuration = {
        ...this.configuration,
        ...config,
        host: isMobileRegEx.test(window.navigator.userAgent) ? "pda" : "web",
      };
    }
  }

  /** Метод лля получения текущего конфига трекинга */
  getConfiguration(): ITrackerConfig {
    return this.configuration;
  }

  /** Добавляет отправку эвента в очередь событий менеджера трекера
   * тип any для первого аргумента добавлен при рефакторинге для сохранения интерфейса использования, чтобы не менять
   * вызов функции по всему коду, а также на старом стеке и читалке
   * @param _ первый аргумент обязателен, но он игнорируется
   * @param event параметры для отправки аналитики
   * */
  sendEvent<T extends object>(_: any, event: IEventData<T>) {
    if (this.isSendingEventsAvailable()) {
      const analyticsEvent = getTrackerEvent<T>(event);
      this.queue.push(analyticsEvent);
      this.saveQueueToStorage();
    } else if (this.configuration.isTester) {
      const fullEventData = prepareFullEventDataForSending(
        getTrackerEvent(event),
        this.configuration
      );
      this.printAnalyticsEventToConsole(fullEventData);
    }
  }

  // Этот метод - обертка для метода sendEvent, который отправляет событие в трекер
  // Для того, чтобы не рефакторить пол-Литреса - можно постепенно добавлять типы событий в TEventMapCommon
  // и использовать этот метод вместо sendEvent
  public sendEventTyped<K extends keyof TEventMapCommon>(
    eventType: K,
    params: TEventMapCommon[K]
  ) {
    // Первый аргумент игнорируется, так как он был добавлен для сохранения интерфейса использования
    this.sendEvent<TEventMapCommon[K]>(null, { eventType, params });
  }

  /** Отправляет событие без сохранения в очередь - предполагается использовать для событий,
   * в момент закрытия страницы
   * @param event IEventData параметры для отправки аналитики
   * */
  public sendEventSync(event: IEventData) {
    if (this.isSendingEventsAvailable()) {
      const analyticsEvent = prepareFullEventDataForSending(
        getTrackerEvent(event),
        this.configuration
      );
      const blobData = new Blob([JSON.stringify(analyticsEvent)], {
        type: "application/json",
      });
      try {
        navigator.sendBeacon(this.endpoint, blobData);
      } catch (err) {
        console.log(err);
      }
    } else if (this.configuration.isTester) {
      const analyticsEvent = prepareFullEventDataForSending(
        getTrackerEvent(event),
        this.configuration
      );
      this.printAnalyticsEventToConsole(analyticsEvent);
    }
  }

  private async sendEventsFromQueue() {
    if (this.queue.length === 0 || this.isSendingEventsFromQueue) return;

    this.isSendingEventsFromQueue = true;

    // Обработка событий по одному
    for (let i = 0; i < this.queue.length; i++) {
      const currentEvent = this.queue[i];

      if (this.proceedingEventUUIDs.has(currentEvent.eventUUID)) {
        continue;
      }

      this.proceedingEventUUIDs.add(currentEvent.eventUUID);

      let attempt = 0;
      let success = false;

      while (attempt < this.maxRetries && !success) {
        try {
          await axios.post<unknown, unknown, ITrackerRequestData>(
            this.endpoint,
            prepareFullEventDataForSending(currentEvent, this.configuration),
            {
              headers: {
                "Session-Id": getCookie("SID") || "",
                /** Этот заголовок необходим foundation для определения с какой платформы пришел запрос
                 * для web/pda это всегда 115
                 * https://master.api.b.litres.ru/docs/?urls.primaryName=tracker#/default/create_event_api_event__post
                 */
                "App-id": "115",
              },
            }
          );
          success = true;
        } catch (error) {
          console.error(
            `Analytics send failed for event '${currentEvent}' (attempt ${attempt + 1})`,
            error
          );
          attempt++;
        }
      }

      if (success) {
        // Удаляем успешно отправленное событие из очереди
        this.queue.splice(i, 1);
        i--; // Уменьшаем индекс, чтобы не пропустить следующий элемент
        this.saveQueueToStorage();
      } else {
        console.error(
          `Max retries reached for event '${currentEvent}' (ID: ${currentEvent.eventUUID}), keeping event in queue.`
        );
      }
    }

    // Очистка очереди
    this.queue = [];
    this.saveQueueToStorage();
    this.proceedingEventUUIDs.clear();

    this.isSendingEventsFromQueue = false;
  }

  private startSendEventsTimer() {
    this.isSendingEventsFromQueue = false;
    this.loadQueueFromStorage();
    this.timer = setInterval(
      () => this.sendEventsFromQueue(),
      this.sendingEventsInterval
    );
  }

  stop() {
    if (this.timer) clearInterval(this.timer);
    this.sendEventsFromQueue();
  }

  private sendEventsFromQueueSync() {
    if (this.queue.length === 0) return;

    // Отправка с помощью sendBeacon
    for (let i = 0; i < this.queue.length; i++) {
      const currentEvent = this.queue[i];

      if (this.proceedingEventUUIDs.has(currentEvent.eventUUID)) {
        continue;
      }

      this.proceedingEventUUIDs.add(currentEvent.eventUUID);

      const blobData = new Blob(
        [
          JSON.stringify(
            prepareFullEventDataForSending(currentEvent, this.configuration)
          ),
        ],
        { type: "application/json" }
      );

      try {
        navigator.sendBeacon(this.endpoint, blobData);
        this.queue.splice(i, 1);
        i--;
        this.saveQueueToStorage();
      } catch (err) {
        console.log(err);
      }
    }
    this.proceedingEventUUIDs.clear();
  }

  private loadQueueFromStorage() {
    const events = getFromSessionStorage(this.STORAGE_KEY);
    if (Array.isArray(events)) {
      this.queue = events.concat(this.queue);
    }
  }

  private saveQueueToStorage() {
    saveToSessionStorage({ [this.STORAGE_KEY]: this.queue });
  }

  private printAnalyticsEventToConsole(fullEventData: ITrackerRequestData) {
    console.debug(
      `ANALYTICS: %c${fullEventData.event_type}\n`,
      "font-weight: bold; color: #ff4c00;", // стили для event_type (за счёт директивы %с)
      fullEventData
    );
  }
}

export const TM = TrackerManager.getInstance();

// Необходимо для импорта в ES5
export default TrackerManager;
